import { Bar, BarChart, XAxis, YAxis } from "recharts";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@volca/ui/components/ui/chart";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { PROFITABILITY_PALETTE } from "@/core/colors";
import { useReportCurrency } from "@/profitability/currency";
import { isMissing, MISSING_LABEL } from "@/profitability/missing";
import { showValue } from "@/profitability/missing-value";
import {
  COLUMN_ROLE,
  type ReportColumn,
  type ReportDataset,
  type ReportRow,
} from "@/profitability/types";
import { columnLabel, formatReportValue } from "./utils";

const COST_COLORS = PROFITABILITY_PALETTE.costs;
const LEFT = "__left";
/** Recharts corner radii, read clockwise from the top left. */
const OPEN_LEFT: [number, number, number, number] = [4, 0, 0, 4];
const OPEN_RIGHT: [number, number, number, number] = [0, 4, 4, 0];
const SQUARE: [number, number, number, number] = [0, 0, 0, 0];

export const moneyRoles = (dataset: ReportDataset) => ({
  total: dataset.columns.find((item) => item.role === COLUMN_ROLE.total),
  costs: dataset.columns.filter((item) => item.role === COLUMN_ROLE.cost),
  result: dataset.columns.find((item) => item.role === COLUMN_ROLE.result),
});

/**
 * Where a row's money went, as one bar.
 *
 * The dataset marks its own columns, so the app never has to work out which of
 * ten currency figures is a cost. It cannot be worked out: several sets of
 * columns add to the total on every row, and one of them puts profit before
 * burden in with the costs. A job that earned $7,922 and paid out $3,690 then
 * reads as one bar filling left to right, not as eight figures to subtract by
 * eye.
 *
 * It is a stacked bar from the same chart library as the rest of the report,
 * so its segments meet with no seam and it carries the hover readout every
 * other chart on the page has.
 */
export const RowComposition = ({
  row,
  dataset,
}: {
  row: ReportRow;
  dataset: ReportDataset;
}) => {
  const currency = useReportCurrency();
  const { total: totalColumn, costs, result } = moneyRoles(dataset);
  const total = totalColumn ? Number(row[totalColumn.key] ?? 0) : 0;
  if (!totalColumn || !Number.isFinite(total) || total <= 0) return null;

  const parts = costs
    .map((column, index) => ({
      key: column.key,
      column,
      value: Number(row[column.key] ?? 0),
      color: COST_COLORS[index % COST_COLORS.length],
    }))
    .filter((part) => Number.isFinite(part.value) && part.value > 0);
  const spent = parts.reduce((sum, part) => sum + part.value, 0);
  // A result not in yet is missing, not zero (FIN-004): the bar shows the
  // costs and says the rest is missing rather than drawing nothing left.
  const leftMissing = !!result && isMissing(row[result.key]);
  const left = result
    ? leftMissing
      ? 0
      : Number(row[result.key])
    : total - spent;
  const over = left < 0;
  // What is left is the point of the bar, so it is drawn even at zero, and in
  // the page's own ink rather than another hue from the cost palette.
  const segments: {
    key: string;
    column?: ReportColumn;
    value: number;
    color: string;
  }[] = [
    ...parts,
    {
      key: LEFT,
      column: result,
      value: Math.abs(left),
      color: over ? PROFITABILITY_PALETTE.loss : PROFITABILITY_PALETTE.result,
    },
  ];
  const leftLabel = result ? columnLabel(result) : "Left over";
  const label = (segment: (typeof segments)[number]) =>
    segment.column ? columnLabel(segment.column) : leftLabel;
  const amount = (segment: (typeof segments)[number]) =>
    formatReportValue(
      segment.key === LEFT ? (leftMissing ? null : left) : segment.value,
      segment.column ?? totalColumn,
      currency,
    );
  // The bar is divided by what it draws, not by the total.
  //
  // Those are usually the same figure. They part company on a row that carries
  // a credit, such as a reversed labor burden, which is left out of the bar
  // because a negative width cannot be drawn: the costs then come to more than
  // the revenue. Dividing by the total there would run the segments past the
  // end of the bar and print shares adding to 268 percent.
  const basis = segments.reduce((sum, segment) => sum + segment.value, 0);
  const share = (value: number) =>
    basis > 0 ? `${Math.round((value / basis) * 100)}%` : "";
  // Smallest first, so the bar grows left to right and the slivers sit together
  // at the end they can be read from rather than scattered along it. A colour
  // still belongs to its column, so sorting moves a segment without renaming
  // it.
  segments.sort((a, b) => a.value - b.value);
  const drawn = segments.filter((segment) => segment.value > 0);
  const config: ChartConfig = Object.fromEntries(
    drawn.map((segment) => [
      segment.key,
      { label: label(segment), color: segment.color },
    ]),
  );
  // Switching rows with the panel open changes which bars exist and in what
  // order, and recharts keeps the stack offsets it worked out for the row
  // before: segments land on top of each other, or leave a gap where one used
  // to be. The chart is keyed by what it draws, so a different row builds a
  // different chart rather than editing this one.
  const identity = drawn
    .map((segment) => `${segment.key}:${segment.value}`)
    .join("|");
  const data = [
    Object.fromEntries(drawn.map((segment) => [segment.key, segment.value])),
  ];

  return (
    <Stack className="w-full" gap={2} align="stretch">
      <Stack direction="row" justify="between" align="baseline" gap={2}>
        <Text weight="semibold" variant="caption" tone="muted">
          {columnLabel(totalColumn)}
        </Text>
        <Text variant="label">
          {formatReportValue(total, totalColumn, currency)}
        </Text>
      </Stack>
      <ChartContainer
        key={identity}
        config={config}
        // Two pixels taller than the bar itself. The segments carry a one
        // pixel stroke, and at exactly the bar's height the outer half of it
        // is clipped by the top and bottom of the drawing, which flattens the
        // rounded ends unevenly.
        className="aspect-auto h-7 w-full"
        role="img"
        aria-label={`${leftLabel}: ${leftMissing ? MISSING_LABEL.toLowerCase() : formatReportValue(left, result ?? totalColumn, currency)} of ${formatReportValue(total, totalColumn, currency)}`}
      >
        <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          barCategoryGap={0}
        >
          <XAxis type="number" domain={[0, basis]} hide />
          <YAxis type="category" hide />
          <ChartTooltip
            cursor={false}
            // One segment at a time. A stacked bar shares its tooltip across
            // the whole stack by default, which answers a question the reader
            // did not ask: they are pointing at one cost.
            shared={false}
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(value, name) => {
                  const segment = drawn.find((item) => item.key === name);
                  if (!segment) return null;
                  return (
                    <Stack
                      direction="row"
                      align="baseline"
                      gap={2}
                      className="w-full min-w-0"
                    >
                      <span className="min-w-0 flex-1">{label(segment)}</span>
                      <span className="flex-none text-subtle">
                        {share(Number(value))}
                      </span>
                      <span className="flex-none font-semibold">
                        {showValue(amount(segment))}
                      </span>
                    </Stack>
                  );
                }}
              />
            }
          />
          {drawn.map((segment, index) => (
            <Bar
              key={segment.key}
              dataKey={segment.key}
              stackId="money"
              fill={segment.color}
              // Segment boundaries land on fractions of a pixel, and two paths
              // that share one are each drawn with partial coverage of it, so
              // the page shows through as a hairline. A stroke of the segment's
              // own colour covers that half pixel on both sides.
              stroke={segment.color}
              strokeWidth={1}
              // Nothing to animate: one datum, drawn once, replaced wholesale
              // when the reader opens another row.
              isAnimationActive={false}
              maxBarSize={24}
              radius={
                drawn.length === 1
                  ? [4, 4, 4, 4]
                  : index === 0
                    ? OPEN_LEFT
                    : index === drawn.length - 1
                      ? OPEN_RIGHT
                      : SQUARE
              }
            />
          ))}
        </BarChart>
      </ChartContainer>
      {/* One line per segment in the sidebar, where a name, a share and an
          amount do not fit side by side twice over; two up where there is
          room for them. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-x-5 gap-y-1">
        {segments.map((segment) => (
          <Stack
            key={segment.key}
            direction="row"
            align="baseline"
            gap={1}
            className="min-w-0"
          >
            <span
              aria-hidden
              style={{ backgroundColor: segment.color }}
              className="size-2 flex-none translate-y-px rounded-sm"
            />
            <Text variant="caption" tone="muted" className="min-w-0 flex-1">
              {label(segment)}
            </Text>
            <Text variant="caption" tone="muted" className="flex-none">
              {segment.key === LEFT && leftMissing ? "" : share(segment.value)}
            </Text>
            <Text weight="semibold" variant="caption" className="flex-none">
              {showValue(amount(segment))}
            </Text>
          </Stack>
        ))}
      </div>
    </Stack>
  );
};
